// custom: T8a PR3b — normaliza resultados da SearchAPI para o shape do item da lista
//
// O endpoint /search/conversations retorna um shape DIFERENTE do item de
// conversa da store (usado pelos ConversationCard). Este normalizer adapta
// cada resultado server-side antes de entrar no merge com a lista local, para
// que ConversationCard.vue e getLastMessage não recebam um shape inesperado.
//
// Shape da SearchAPI (conversations.json.jbuilder):
//   { id, account_id, created_at (int), message (objeto), contact (objeto),
//     inbox (objeto), agent (objeto), additional_attributes }
//
// Shape do contact na SearchAPI (_contact.json.jbuilder):
//   { id, email, name, phone_number, identifier, additional_attributes,
//     last_activity_at }
//   NÃO inclui: thumbnail, availability_status
//
// REGRA CRÍTICA para o sender: incluir APENAS os campos que a SearchAPI
// realmente fornece. SET_CONTACT_ITEM faz merge raso ({...existente, ...novo});
// incluir thumbnail:'' ou availability_status:null SOBRESCREVERIA valores já
// carregados na store (zera avatar e presença de contato em todo o app).
//
// Shape esperado pelos cards (store item):
//   { id, inbox_id, status, unread_count, timestamp, created_at, labels,
//     priority, sla_policy_id, messages (array), meta: { sender, assignee } }

/**
 * Converte um resultado da SearchAPI (/search/conversations) para o shape
 * mínimo compatível com ConversationCard / getLastMessage.
 *
 * Defaults na CONVERSA (não no sender):
 *   - unread_count = 0 (não sabemos; o item real na store tem o valor certo)
 *   - status = 'open' (default defensivo — não afeta filtros da aba)
 *   - labels = [] (sem labels visíveis para resultados de histórico)
 *   - priority = null
 *   - sla_policy_id = null
 *
 * @param {Object} raw - objeto cru da SearchAPI
 * @returns {Object} - item no shape do item de conversa da store
 */
export function normalizeSearchResult(raw) {
  if (!raw || typeof raw !== 'object') return null;

  // message (singular) → messages (array) para getLastMessage
  const messages = raw.message ? [raw.message] : [];

  // contact → meta.sender: APENAS os campos que a SearchAPI retorna.
  // NÃO inclui thumbnail nem availability_status para não sobrescrever
  // valores existentes na store quando SET_CONTACT_ITEM for chamado.
  const contact = raw.contact ?? {};
  const sender = {
    id: contact.id ?? null,
    name: contact.name ?? '',
    phone_number: contact.phone_number ?? null,
    additional_attributes: contact.additional_attributes ?? null,
    // email e identifier incluídos se presentes (são da API, não destrutivos)
    ...(contact.email != null && { email: contact.email }),
    ...(contact.identifier != null && { identifier: contact.identifier }),
    ...(contact.last_activity_at != null && {
      last_activity_at: contact.last_activity_at,
    }),
  };

  return {
    id: raw.id,
    account_id: raw.account_id,
    inbox_id: raw.inbox?.id ?? null,
    created_at: raw.created_at,
    timestamp: raw.created_at, // SearchAPI não expõe timestamp separado
    status: 'open', // default defensivo — não afeta filtros da aba
    unread_count: 0, // desconhecido no resultado de busca; card trata 0 como lido
    labels: [],
    priority: null,
    sla_policy_id: null,
    messages,
    meta: {
      sender,
      assignee: raw.agent ?? null,
    },
  };
}
